import { Env } from "@/env";
import { useUser } from "@features/user/use-user";
import { isArray } from "lodash";
import { useState } from "react";
import { useInfiniteQuery, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { fetchServer } from "../utils";
import { ImagesType } from "./type";
import { useImageActions } from "./use-images-actions";

export const useForceLowQualityImages = () => {
  const { user } = useUser();
  return (
    user.data?.plan === "free" &&
    user.data?.credits_used < 100 &&
    (user.data?.credits || 0) + (user.data?.additional_credits || 0) < 100
  );
};

export const useImage = (id: string) => {
  const {
    remove,
    restore,
    download,
    upscale,
    magic,
    changeProjectId,
    restyle,
    extend,
    background,
    createVideo,
    tryOn,
    haircut,
    faceEffect,
    saving,
  } = useImageActions(id);
  const queryClient = useQueryClient();
  const [pendingUpdate, setPendingUpdate] = useState(false);
  const updateCache = useUpdateImageCache();
  const forceLowQuality = useForceLowQualityImages();

  const applyWatermark = (img: ImagesType) => {
    if (forceLowQuality) {
      return { ...img, url: `${Env.server}/api/images/${img.id}/prev1` };
    }
    return img;
  };

  const getPreloadedImage = (id: string) => {
    const matchingKeys = queryClient
      .getQueryCache()
      .getAll()
      .filter(
        (arg: any) =>
          arg.queryKey[0] === "images" && arg.queryKey[1] === "list",
      );

    for (const key of matchingKeys) {
      const images = queryClient.getQueryData<{
        pages: { data: ImagesType[] }[];
      }>(key.queryKey);
      // Flatten the images.pages pages
      if (images && images.pages.length > 0) {
        const flattenedImages = images.pages.flatMap((page) => page.data);
        const foundImage = flattenedImages.find((img) => img.id === id);
        if (foundImage) return applyWatermark(foundImage);
      }
    }

    return undefined;
  };

  const image = useQuery(
    ["images", id],
    async () => {
      const data = await fetchServer<ImagesType>("/api/images/" + id);
      const processedData = applyWatermark(data);
      updateCache([processedData]);
      return processedData;
    },
    {
      initialData: () => getPreloadedImage(id),
      enabled: !getPreloadedImage(id),
    },
  );

  return {
    image: image.data,
    refresh: image.refetch,
    pendingUpdate: saving || pendingUpdate,
    setPendingUpdate,
    remove: (redirectTo?: string) =>
      remove(image.data, setPendingUpdate, redirectTo),
    restore: () => restore(image.data, setPendingUpdate),
    download: () => download(image.data),
    upscale: (resolution: number) => upscale(image.data, resolution),
    magic: () => magic(image.data),
    changeProjectId: (projectId: string) =>
      changeProjectId(image.data, projectId),
    restyle: (
      styleId: string,
      fromImage?: File | string,
      prompt?: string,
      count?: number,
    ) => restyle(image.data, styleId, fromImage, prompt, count),
    extend: (
      imageData: string,
      prompt?: string,
      count?: number,
      attachments?: string[],
      loraId?: string,
      chips?: {
        number: number;
        x: number;
        y: number;
        prompt: string;
        attachment?: string;
        lora_id?: string;
      }[],
      basic = false,
    ) =>
      extend(
        image.data,
        imageData,
        prompt,
        count,
        attachments,
        loraId,
        chips,
        basic,
      ),
    background: (newBackground?: string, prompt?: string, count = 1) =>
      background(image.data, newBackground, prompt, count),
    createVideo: (options: {
      prompt?: string;
      audio?: string | boolean; // Base64 encoded audio
      quality: "low" | "medium" | "high";
      duration: number;
    }) => createVideo(image.data, options),
    tryOn: (fromImage: File | string, count = 1) =>
      tryOn(image.data, fromImage, count),
    haircut: (fromImage: File | string, count = 1) =>
      haircut(image.data, fromImage, count),
    faceEffect: (effect: string, makeupDescription?: string, count = 1) =>
      faceEffect(image.data, effect, makeupDescription, count),
  };
};

export const useImages = (
  query?: string,
  archived?: boolean,
  projectId?: string,
) => {
  query = query?.trim() || "";
  const pageSize = 50;
  const updateCache = useUpdateImageCache();
  const { site } = useParams();
  const forceLowQuality = useForceLowQualityImages();

  const applyWatermark = (images: ImagesType[]) => {
    if (forceLowQuality) {
      return images.map((img) => ({
        ...img,
        url: `${Env.server}/api/images/${img.id}/prev1`,
      }));
    }
    return images;
  };

  const key = ["images", "list", site, archived === true, query, projectId];
  const images = useInfiniteQuery(
    key,
    async ({ pageParam = 0 }) => {
      const response = await fetchServer<ImagesType[]>(
        "/api/images?" +
          new URLSearchParams({
            site: site || "",
            query: query || "",
            project_id: projectId || "",
            limit: pageSize.toString(),
            offset: (pageParam * pageSize).toString(),
          }).toString() +
          (archived ? "&archived=1" : ""),
      );

      const processedResponse = applyWatermark(response);
      updateCache(processedResponse);

      return {
        data: processedResponse,
        nextPage: response.length === pageSize ? pageParam + 1 : undefined,
      };
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextPage,
    },
  );

  // Flatten the pages data for easy access
  const flattenedData = images.data?.pages.flatMap((page) => page.data) || [];

  return {
    images: {
      ...images,
      data: flattenedData || [],
      hasNextPage: images.hasNextPage,
      fetchNextPage: () => {
        images.fetchNextPage();
      },
      isFetchingNextPage: images.isFetchingNextPage || images.isRefetching,
    },
    refresh: images.refetch,
  };
};

export const useUpdateImageCache = () => {
  const queryClient = useQueryClient();

  const updateCache = (updates: ImagesType[], deleted?: boolean) => {
    const matchingKeys = queryClient
      .getQueryCache()
      .getAll()
      .filter(
        (arg: any) =>
          arg.queryKey[0] === "images" && arg.queryKey[1] === "list",
      );

    matchingKeys.forEach((key) => {
      queryClient.setQueryData<ImagesType[]>(key.queryKey, (oldData) => {
        for (const updated of updates) {
          if (oldData && isArray(oldData)) {
            if (deleted) {
              return [...oldData].filter((img) => img.id !== updated.id);
            } else {
              return [...oldData].map((img) =>
                img.id === updated.id ? updated : img,
              );
            }
          }
        }
        return oldData!;
      });
    });

    if (!deleted) {
      for (const updated of updates) {
        queryClient.setQueryData(["images", updated.id], updated);
      }
    }
  };

  return updateCache;
};
